import React, { useState, useEffect, useContext, useMemo } from "react";
import { BrandingContext } from "../BrandingProvider";
import { C } from "./ipd-billing/constants";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import CashCounterDashboard from "./ipd-billing/components/CashCounterDashboard";
import InterimScrollView from "./ipd-billing/components/InterimScrollView";
import FinalScrollReview from "./ipd-billing/components/FinalScrollReview";
import ScrollPrintSheet, { SCROLL_PRINT_STYLES } from "./ipd-billing/components/ScrollPrintSheet";
import { useCashCounter } from "./ipd-billing/useCashCounter";

// ─────────────────────────────────────────────────────────────────────────────
// Cash Counter module (readmd/ipd-cash-counter-flow.md).
//
//   Screen 1  Cash Counter Dashboard
//   Screen 2  Interim Scroll View
//   Screen 3  Final Scroll Review + Finalize
//
// The cash-counter / scroll APIs are TENANT-scoped (not /ipd_admission/{id}/…),
// so the counter needs no patient — there is no patient selector here.
// ─────────────────────────────────────────────────────────────────────────────

const SCREENS = { DASHBOARD: "dashboard", INTERIM: "interim", FINAL: "final" };

const TITLES = {
  [SCREENS.DASHBOARD]: ["Cash Counter", "Session, live totals & end-of-day scroll"],
  [SCREENS.INTERIM]: ["Interim Scroll", "Live session totals & transaction snapshot"],
  [SCREENS.FINAL]: ["Final Scroll Review", "Reconcile declared cash and close the day"],
};

export default function IPDBillingCashCounter() {
  const [screen, setScreen] = useState(SCREENS.DASHBOARD);
  const cc = useCashCounter();

  const [title, subtitle] = TITLES[screen];

  // Printed-scroll letterhead: logo + brand statement from BrandingContext (same
  // source the invoice/prescription prints use), facility name & address from the
  // session the cashier logged in with.
  const brandingData = useContext(BrandingContext);
  const hospital = useMemo(() => {
    const ss = (k) => {
      try { return sessionStorage.getItem(k) || ""; } catch { return ""; }
    };
    const collateralImage =
      brandingData?.find((item) => item.name === "collateral_image")?.content || null;
    const brandStatement =
      brandingData?.find((item) => item.name === "branding_statements")?.content
        ?.collateralBrandStatement || "";

    return {
      // `facility_name` holds the facility *id*; the display name is separate.
      name: ss("facility_display_name") || ss("facility_name"),
      address: [ss("facilityAddressLine1"), ss("facilityAddressLine2"), ss("facilityAddressLine3")]
        .filter(Boolean)
        .join(", "),
      logo: collateralImage ? `data:image/png;base64,${collateralImage}` : null,
      brandStatement,
    };
  }, [brandingData]);

  // A successful Print Interim sets cc.printJob (a fresh object each time). Let
  // React paint the print sheet, then open the browser print dialog → Save as PDF.
  useEffect(() => {
    if (!cc.printJob) return;
    const t = setTimeout(() => window.print(), 80);
    return () => clearTimeout(t);
  }, [cc.printJob]);

  return (
    <IPDBillingLayout activeNav="cash-counter">
      <style>{SCROLL_PRINT_STYLES}</style>
      {cc.printJob && (
        <ScrollPrintSheet
          scroll={cc.printJob.scroll}
          session={cc.printJob.session}
          counterCode={cc.printJob.counterCode}
          hospital={hospital}
          kind={cc.printJob.kind}
          reconciliation={cc.printJob.reconciliation}
        />
      )}
      <div className="ipd-scroll-no-print" style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>
        <div>
          <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>{title}</h2>
          <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>{subtitle}</p>
        </div>

        {screen === SCREENS.INTERIM ? (
          <InterimScrollView cc={cc} onBack={() => setScreen(SCREENS.DASHBOARD)} onReviewFinal={() => setScreen(SCREENS.FINAL)} />
        ) : screen === SCREENS.FINAL ? (
          <FinalScrollReview cc={cc} onBack={() => setScreen(SCREENS.DASHBOARD)} onClosed={() => setScreen(SCREENS.DASHBOARD)} />
        ) : (
          <CashCounterDashboard
            cc={cc}
            onViewInterim={() => setScreen(SCREENS.INTERIM)}
            onReviewFinal={() => setScreen(SCREENS.FINAL)}
          />
        )}
      </div>
    </IPDBillingLayout>
  );
}
